/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type {
  CommunityPromptPostPrayer,
  PromptPostPrayer,
  ReflectionPostPrayer,
} from "@packages/sdk";
import type { MouseEventHandler } from "react";

import type { ThemeType } from "../../../../../../global/emotion";
import { radius, spacing } from "../../../../../../global/emotion";
import { Button } from "../../../../../components/_base/_core/Button/Button.emotion";
import { Text } from "../../../../../components/_base/_core/Text/Text.emotion";
import { ContentSmall } from "../../../../../components/_base/content/content/ContentSmall/ContentSmall.emotion";

const reflectionButtonStyle: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.higherContrastVeryLow,
  borderRadius: radius.ms,
  color: theme.semanticColors.neutralsHigh,
  display: "grid",
  gap: spacing.ml,
  gridTemplateAreas: "'text' 'content'",
  gridTemplateColumns: "1fr",
  gridTemplateRows: "1fr 1fr",
  height: "auto",
  maxWidth: 320,
  padding: spacing.ml,
  width: spacing.full,
});
const reflectionTextStyle: Interpolation = {
  display: "block",
  minWidth: 0,
  overflow: "hidden",
  placeSelf: "start",
  whiteSpace: "normal",
  width: spacing.full,
};
const promptStyle: Interpolation = {
  textAlign: "center",
};
const contentStyle: Interpolation = {
  maxWidth: 280,
  minWidth: 0,
  padding: spacing.none,
  width: spacing.full,
};

export const PostPrayerReflectionBody = ({
  step,
}: {
  step: ReflectionPostPrayer | PromptPostPrayer | CommunityPromptPostPrayer;
}) => {
  const t = useTranslations();

  return (
    <Text as={"p"} type={"quote"} size={"xl"} css={promptStyle}>
      {step.session?.prompt ?? t("post_prayer_reflection_question")}
    </Text>
  );
};

export const PostPrayerReflectionButton = ({
  step,
  onClick,
}: {
  step: ReflectionPostPrayer | PromptPostPrayer | CommunityPromptPostPrayer;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) => {
  const analytics = useAnalytics();
  const t = useTranslations();

  return (
    <Button
      css={reflectionButtonStyle}
      onClickCapture={() => {
        analytics.track({
          event: HallowAnalyticsEvent.TappedReflectOnPostPrayer,
          properties: {
            prayer: step.session.id,
          },
        });
      }}
      onClick={onClick}
    >
      <>
        <Text as={"span"} type={"body"} size={"m"} css={reflectionTextStyle}>
          {t("post_prayer_reflection_prompt")}
        </Text>
        <ContentSmall
          title={step.session.title}
          imgSrc={step.session.images}
          description={step.session.label_desc}
          disabled={true}
          size={"xs"}
          css={contentStyle}
        />
      </>
    </Button>
  );
};
